import React, {Fragment} from 'react';
import {useQueryClient} from "react-query";
import __ from "lodash";
import moment from "moment";
import Avatar from "@material-ui/core/Avatar";
import {useTheme, withStyles} from "@material-ui/core/styles";
import AogActivity from "./AogActivity";
import {useAircraftMovementsList} from "../../../services/aircraft";
import {LightTooltip} from "../../../controls";
import Icons from '../../../icons';

const StyledAvatar = withStyles((theme) => ({
  root: {
    width: 30,
    height: 30,
  },
}))(Avatar);

const AircraftStatus = (props) => {

  const externalAircraft = props.external;

  const theme = useTheme();
  const queryClient = useQueryClient();
  const extraKeys = [externalAircraft ? "ZZ-ZZZ" : props.aircraftTailNumber];

  const aircraftMovements = useAircraftMovementsList({queryClient, extraKeys});
  const currentMovement = externalAircraft ? props.movement : __.head(aircraftMovements.data);

  let origin = "";
  let destination = "";
  let tooltip = "";
  let source = "images/AircraftOnGroundIcon.png";
  let icon = "AirplaneParkedIcon";
  let lastMovement = undefined;
  const aogTextColor = theme.palette.aogCard.contrastText;
  const maintenanceTextColor = theme.palette.maintenanceCard.contrastText;
  const returnToStandTextColor = theme.palette.returnToStandCard.contrastText;
  const aogColor = theme.palette.aogCard.backgroundColor;
  const maintenanceColor = theme.palette.maintenanceCard.backgroundColor;
  const returnToStandColor = theme.palette.returnToStandCard.backgroundColor;
  const externalColor = theme.palette.externalCard.backgroundColor;
  let statusTextColor = externalAircraft ? theme.palette.externalCard.contrastText : theme.palette.card.contrastText;
  let statusColor = externalAircraft ? theme.palette.externalCard.backgroundColor : theme.palette.card.content.contrastText;
  let displayLastMovement = props.displayLastMovement;
  let displayLastMovementOnSingleLine = props.displayLastMovementOnSingleLine;

  const renderLastMovement = (movement, movementDateTime) => {
    const movementTime = movementDateTime.includes("Z") ? moment.utc(movementDateTime).format("HH:mm") : moment(movementDateTime).format("HH:mm");
    const movementDate = movementDateTime.includes("Z") ? moment.utc(movementDateTime).format("DD/MM/YYYY") : moment(movementDateTime).format("DD/MM/YYYY");
    if (displayLastMovementOnSingleLine)
      return (<span>{movement} at {movementTime} on {movementDate}</span>);
    return (<span>{movement} at {movementTime}<br/>on {movementDate}</span>);
  }

  const renderMaintenance = (check, start, end, nextUpdate) => {
    const startTime = start.includes("Z") ? moment.utc(start).format("HH:mm") : moment(start).format("HH:mm");
    const startDate = start.includes("Z") ? moment.utc(start).format("DD/MMM") : moment(start).format("DD/MMM");
    const endTime = end.includes("Z") ? moment.utc(end).format("HH:mm") : moment(end).format("HH:mm");
    const endDate = end.includes("Z") ? moment.utc(end).format("DD/MMM") : moment(end).format("DD/MMM");

    return (
      <Fragment>
        <span>{check}{check === "AOG" ? "" : " check"}<br/>{startDate} to {endDate}</span>
        {nextUpdate ? <span><br/>{nextUpdate}</span> : <></>}
      </Fragment>

    );
  }

  if (currentMovement != null) {
    if (__.has(currentMovement,"activeMaintenance")){
      source = "images/AircraftMaintenance.png";
      icon = "AirplaneMxIcon";
      destination = currentMovement.maintenanceAirport;
      statusTextColor = maintenanceTextColor;
      statusColor = maintenanceColor;
      if (__.has(currentMovement,"scheduledMaintenance") && currentMovement.scheduledMaintenance) {
        tooltip = `${props.aircraftTailNumber} is undergoing ${currentMovement.aircraftCheck} check`;
        lastMovement = renderMaintenance(currentMovement.aircraftCheck, currentMovement.startCheckDate, currentMovement.endCheckDate);
      } else if ( __.has(currentMovement,"activeMaintenance") && currentMovement.activeMaintenance) {
        source = "images/AircraftMaintenanceIcon.png";
        if (__.has(currentMovement,"aogActivities"))
          tooltip = <AogActivity activity={currentMovement.aogActivities}/>
        else
          tooltip =  `${props.aircraftTailNumber} is on the ground in ${destination}`;
        lastMovement = renderMaintenance(currentMovement.aircraftCheck, currentMovement.startCheckDate, currentMovement.endCheckDate, currentMovement.nextUpdate);
      }
    }
    else if (currentMovement.movementType === 'RETURN-TO-STAND') {
      source = "images/AircraftOnGroundIcon.png";
      icon = "AirplaneReturnIcon";
      destination = currentMovement.destinationAirportCodeIata;
      tooltip = `${props.aircraftTailNumber} returned to stand`;
      lastMovement = (<span>Returned to Stand</span>);
      statusTextColor = returnToStandTextColor;
    }
    else if (currentMovement.onBlocksTimeUTC !== null) {
      source = "images/AircraftOnGroundIcon.png";
      icon = "AirplaneParkedIcon";
      destination = currentMovement.destinationAirportCodeIata;
      tooltip = `${props.aircraftTailNumber} is on the ground in ${destination}`;
      lastMovement = renderLastMovement("Chocks-on", currentMovement.onBlocksTimeUTC);
    }
    else if (currentMovement.landingTimeUTC !== null) {
      source = "images/AircraftOnGroundIcon.png";
      icon = "AirplaneLandingIcon";
      destination = currentMovement.destinationAirportCodeIata;
      tooltip = `${props.aircraftTailNumber} is on the ground in ${destination}`;
      lastMovement = renderLastMovement("landed", currentMovement.landingTimeUTC);
    }
    else if (currentMovement.takeOffTimeUTC !== null) {
      source = "images/AircraftEnRouteIcon.png";
      icon = "AirplaneEnrouteIcon";      
      origin = currentMovement.originAirportCodeIata;
      destination = currentMovement.destinationAirportCodeIata;
      tooltip = `${props.aircraftTailNumber} is en-route from ${origin} to ${destination}`;
      lastMovement = renderLastMovement("Airborne", currentMovement.takeOffTimeUTC);
    }
    else if (currentMovement.offBlocksTimeUTC !== null) {
      source = "images/AircraftTakeOffIcon.png";
      icon = "AirplaneTakeOffIcon";
      origin = currentMovement.originAirportCodeIata;
      tooltip = `${props.aircraftTailNumber} is taxiing for take-off in ${origin}`;
      lastMovement = renderLastMovement("Pushed-back", currentMovement.offBlocksTimeUTC);
    }
    else {displayLastMovement = false};
  }

  return (
    <div style={{marginTop: 5}}>
      <div style={{display:"flex", justifyContent: "center"}}>
        <div style={{height: props.height-10, marginTop: `${props.avatarSize === "small" ? "5px" : "10px"}`, color: `${statusTextColor}`, fontWeight: "bolder", fontSize: props.fontSize, marginRight: origin?props.marginRight:0}}>{origin}</div>
        <LightTooltip title={tooltip} placement={"left"} style={{maxWidth: "none"}}>
          {/* {props.avatarSize === "small"
            ?
              <StyledAvatar
                src={source}
              />
            :
            <Avatar
              src={source}
            />
          } */}
          {props.avatarSize === "small"
            ? <div style={{ fontSize: 24, color: statusColor, height: 32, width: 32, backgroundColor: "#ffffff", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center"}}><Icons style={{ fontSize: 24, color: statusColor}}>{icon}</Icons></div> 
            : <div style={{ fontSize: 32, color: statusColor, height: 40, width: 40, backgroundColor: "#ffffff", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center"}}><Icons style={{ fontSize: 32, color: statusColor}}>{icon}</Icons></div> 
          }
        </LightTooltip>
        <div style={{height: props.height-10, marginTop: `${props.avatarSize === "small" ? "5px" : "10px"}`, color: `${statusTextColor}`, fontWeight: "bolder", fontSize: props.fontSize, marginLeft: destination?props.marginLeft:0}}>{destination}</div>
      </div>
      {displayLastMovement
        ? <div style={{marginTop: 6, color: `${statusTextColor}`, fontWeight: "bolder", display: "block"}}>{lastMovement}</div>
        : <></>
      }
    </div>
  )
}

export default AircraftStatus;
